import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Workspace preferences.
 *
 * Every value here has an effect. That was not true before: the settings drawer
 * offered a default chart type, a default colour family, a density and two
 * "defaults" toggles, and not one of them was read anywhere outside the drawer
 * that set it. A setting that changes nothing is worse than a missing setting,
 * so each of these is now wired to the thing it names (see `applyPrefsToNewChart`,
 * the `data-density` attribute, and the report filter fallback).
 *
 * Stored in localStorage under a versioned key, with a migration from v1 so an
 * existing user keeps their choices.
 */
export type ThemeChoice = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact';
export type LibraryView = 'grid' | 'table';

/** One assistant conversation a person can return to.
 *
 *  The server checkpoints a conversation under its thread id, so an id is all
 *  that is needed to reopen one — what was missing was any record that the
 *  thread had ever existed (AG-2038). */
export interface AssistantThread {
  id: string;
  /** Taken from the first thing the user said in it. */
  title: string;
  /** Whether it was working on a chart or a dashboard when last used. */
  target: 'chart' | 'dashboard';
  /** Epoch ms, for ordering the list and dating each row. */
  updatedAt: number;
}

/** Enough history to be useful, bounded so prefs stay small. */
export const MAX_ASSISTANT_THREADS = 30;

export interface Prefs {
  /** Light, dark, or follow the OS. */
  theme: ThemeChoice;
  /** Spacing and control-height scale. */
  density: Density;
  /** 0.9–1.15 multiplier on the 16px base size. */
  fontScale: number;
  /** Manual sidebar collapse (browse mode). */
  sidebarCollapsed: boolean;
  /** Chart type a new chart starts on. */
  defaultChartType: string;
  /** Colour family seeded into a new chart's palette. `auto` = component default. */
  defaultColorFamily: string;
  /** Derive report filters from the data when a chart declares none. */
  autoFilters: boolean;
  /** Seed a trend + mean overlay on a new time-series chart. */
  defaultOverlays: boolean;
  /** Card grid or table in the library screens. */
  libraryView: LibraryView;
  /** Keep the assistant panel open across navigations. */
  assistantOpen: boolean;
  /**
   * The workspace assistant's thread id.
   *
   * Persisted because the server keys the conversation on it: a fresh id every
   * page load asks the checkpointer for a thread that has never existed, so the
   * conversation could never come back. Empty until the first one is minted.
   */
  workspaceSession: string;
  /** Past workspace conversations, newest first. The active one is
   *  `workspaceSession`; these are the ones a person can go back to. */
  assistantThreads: AssistantThread[];
}

export const DEFAULT_PREFS: Prefs = {
  theme: 'system',
  density: 'comfortable',
  fontScale: 1,
  sidebarCollapsed: false,
  defaultChartType: 'bar',
  defaultColorFamily: 'auto',
  autoFilters: true,
  defaultOverlays: true,
  libraryView: 'grid',
  assistantOpen: false,
  workspaceSession: '',
  assistantThreads: [],
};

const KEY = 'analytics.prefs.v2';
const LEGACY_KEY = 'analytics.prefs.v1';

function clampScale(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.min(1.15, Math.max(0.9, Math.round(n * 100) / 100));
}

/** Drop anything that is not a usable thread record rather than trusting the
 *  shape of whatever is in storage — a corrupt entry must not break the panel. */
function sanitizeThreads(raw: unknown): AssistantThread[] {
  if (!Array.isArray(raw)) return [];
  const out: AssistantThread[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const t = (item && typeof item === 'object' ? item : {}) as Partial<AssistantThread>;
    const id = typeof t.id === 'string' ? t.id.trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      title: typeof t.title === 'string' ? t.title.slice(0, 120) : '',
      target: t.target === 'dashboard' ? 'dashboard' : 'chart',
      updatedAt: Number.isFinite(Number(t.updatedAt)) ? Number(t.updatedAt) : 0,
    });
    if (out.length >= MAX_ASSISTANT_THREADS) break;
  }
  return out;
}

function sanitize(raw: unknown): Prefs {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Partial<Prefs>;
  return {
    theme: row.theme === 'light' || row.theme === 'dark' ? row.theme : 'system',
    density: row.density === 'compact' ? 'compact' : 'comfortable',
    fontScale: clampScale(row.fontScale),
    sidebarCollapsed: Boolean(row.sidebarCollapsed),
    defaultChartType: typeof row.defaultChartType === 'string' && row.defaultChartType ? row.defaultChartType : 'bar',
    defaultColorFamily:
      typeof row.defaultColorFamily === 'string' && row.defaultColorFamily ? row.defaultColorFamily : 'auto',
    autoFilters: row.autoFilters !== false,
    defaultOverlays: row.defaultOverlays !== false,
    libraryView: row.libraryView === 'table' ? 'table' : 'grid',
    assistantOpen: Boolean(row.assistantOpen),
    workspaceSession:
      typeof row.workspaceSession === 'string' ? row.workspaceSession : '',
    assistantThreads: sanitizeThreads(row.assistantThreads),
  };
}

function load(): Prefs {
  try {
    const current = localStorage.getItem(KEY);
    if (current) return sanitize(JSON.parse(current));
    // v1 held a subset of these keys under different defaults; carry them over
    // rather than resetting someone's workspace on upgrade.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) return sanitize(JSON.parse(legacy));
  } catch {
    /* private mode / corrupt value — fall through to defaults */
  }
  return DEFAULT_PREFS;
}

interface PrefsContextValue {
  prefs: Prefs;
  set: <K extends keyof Prefs>(key: K, value: Prefs[K]) => void;
  reset: () => void;
  /** The theme actually in effect once `system` is resolved. */
  resolvedTheme: 'light' | 'dark';
}

const PrefsContext = createContext<PrefsContextValue | null>(null);

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<Prefs>(load);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch {
      /* quota / private mode — the session still works, it just will not persist */
    }
  }, [prefs]);

  // Follow the OS while the choice is `system`.
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme: 'light' | 'dark' =
    prefs.theme === 'system' ? (systemDark ? 'dark' : 'light') : prefs.theme;

  // One place publishes the document-level state every token reads.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolvedTheme;
    root.dataset.density = prefs.density;
    root.style.setProperty('--font-scale', String(prefs.fontScale));
  }, [resolvedTheme, prefs.density, prefs.fontScale]);

  const set = useCallback(<K extends keyof Prefs>(key: K, value: Prefs[K]) => {
    setPrefs((p) => (p[key] === value ? p : { ...p, [key]: value }));
  }, []);

  const reset = useCallback(() => setPrefs(DEFAULT_PREFS), []);

  const value = useMemo<PrefsContextValue>(
    () => ({ prefs, set, reset, resolvedTheme }),
    [prefs, set, reset, resolvedTheme],
  );

  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs(): PrefsContextValue {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error('usePrefs must be used inside PrefsProvider');
  return ctx;
}

// ── Favourites ──────────────────────────────────────────────────────────────

const FAVORITES_KEY = 'analytics.favorites.v1';

function loadFavorites(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
    return new Set(Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

/**
 * Starred charts and dashboards, per browser.
 *
 * Deliberately local: there is no `favorites` column and no endpoint, and
 * inventing a server-side one would mean a backend change this work does not
 * need. The key is unchanged from the previous implementation, so existing
 * stars survive.
 */
export function useFavorites() {
  const [favorites, setFavorites] = useState<Set<string>>(loadFavorites);

  const persist = useCallback((next: Set<string>) => {
    setFavorites(next);
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify([...next]));
    } catch {
      /* ignore */
    }
  }, []);

  const toggle = useCallback(
    (id: string) => {
      setFavorites((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        try {
          localStorage.setItem(FAVORITES_KEY, JSON.stringify([...next]));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [],
  );

  const isFavorite = useCallback((id: string) => favorites.has(id), [favorites]);

  return { favorites, isFavorite, toggle, replace: persist };
}

/** Internal, exposed for tests: the normaliser every load runs through. */
export const __testing = { sanitize };
